import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Checkbox } from '../../ui/checkbox';
import { toast } from 'sonner';
import { Image as ImageIcon, Trash2, Edit2, Download, X, Check, Loader2, ExternalLink, AlertTriangle, ImageOff, FileText } from 'lucide-react';
import { useAuthStore } from '../../../../store/authStore';
import { downloadAsset } from '../../../lib/downloadAsset';
import axiosInstance from '../../../lib/axios';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const ImageGallery = ({ open, onOpenChange, onSelectImage, cleanupOnly = false }) => {
  const { token } = useAuthStore();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editAltText, setEditAltText] = useState('');
  const [selectedImageForPreview, setSelectedImageForPreview] = useState(null);
  const [deleteConfirmImage, setDeleteConfirmImage] = useState(null);

  // --- Cleanup (gallery cleanup) state ---
  const [cleanupOpen, setCleanupOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [usageMap, setUsageMap] = useState({});
  const [usageLoading, setUsageLoading] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  const [batchDeleting, setBatchDeleting] = useState(false);

  // Fetch images when dialog opens
  useEffect(() => {
    if (open) {
      fetchImages();
    }
  }, [open]);

  // In cleanupOnly mode, jump directly to the cleanup modal when opened
  useEffect(() => {
    if (open && cleanupOnly) {
      openCleanup();
    }
  }, [open, cleanupOnly]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/images/gallery`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      toast.error(error.response?.data?.detail || 'Error al cargar la galería');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (image) => {
    setEditingId(image.id);
    setEditAltText(image.alt_text || '');
  };

  const handleSaveEdit = async (imageId) => {
    try {
      await axios.patch(
        `${API}/images/gallery/${imageId}`,
        { alt_text: editAltText },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success('Imagen actualizada');
      setEditingId(null);
      fetchImages();
    } catch (error) {
      console.error('Error updating image:', error);
      toast.error('Error al actualizar la imagen');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditAltText('');
  };

  const handleDelete = async (imageId) => {
    try {
      console.log('Deleting image:', imageId);
      console.log('Token:', token ? 'Present' : 'Missing');

      const response = await axios.delete(`${API}/images/gallery/${imageId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      console.log('Delete response:', response);
      toast.success('Imagen eliminada');
      setDeleteConfirmImage(null);
      fetchImages();
    } catch (error) {
      console.error('Error deleting image:', error);
      console.error('Error response:', error.response);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Error al eliminar la imagen');
    }
  };

  const handleSelectImage = (image) => {
    setSelectedImageForPreview(image);
  };

  const handleInsertImage = () => {
    if (selectedImageForPreview) {
      // Insert the canonical /assets path when present (ImageNodeView signs it
      // on render); fall back to the URL for external/legacy images.
      const insertSrc = selectedImageForPreview.path || selectedImageForPreview.url;
      onSelectImage(insertSrc, selectedImageForPreview.alt_text || '');
      onOpenChange(false);
      setSelectedImageForPreview(null);
    }
  };

  // --- Cleanup helpers ---
  const openCleanup = async () => {
    setCleanupOpen(true);
    setSelectedIds(new Set());
    setConfirmStep(false);
    setUsageMap({});
    setUsageLoading(true);
    try {
      const res = await axiosInstance.get('/images/gallery/usage');
      setUsageMap(res.data || {});
    } catch (e) {
      console.error('Error fetching usage:', e);
      toast.error('No se pudo cargar el uso de imágenes');
      setUsageMap({});
    } finally {
      setUsageLoading(false);
    }
  };

  const closeCleanup = () => {
    setCleanupOpen(false);
    setConfirmStep(false);
    setSelectedIds(new Set());
    if (cleanupOnly) {
      onOpenChange && onOpenChange(false);
    }
  };

  const isReferenced = (imageId) => {
    const usage = usageMap[imageId];
    return Array.isArray(usage) && usage.length > 0;
  };

  const selectableIds = () => images.filter((img) => !isReferenced(img.id)).map((img) => img.id);

  const allSelectableSelected = () => {
    const sIds = selectableIds();
    return sIds.length > 0 && sIds.every((id) => selectedIds.has(id));
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelectableSelected()) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableIds()));
    }
  };

  const openLightbox = (image) => {
    window.dispatchEvent(
      new CustomEvent('openImageLightbox', {
        detail: { src: image.url || image.path, alt: image.alt_text },
      })
    );
  };

  const handleBatchDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBatchDeleting(true);
    try {
      const res = await axiosInstance.post('/images/gallery/delete', { ids });
      const deleted = res.data?.deleted || [];
      const skipped = res.data?.skipped || [];
      if (deleted.length > 0) {
        toast.success(`${deleted.length} imagen(es) eliminada(s)`);
      }
      if (skipped.length > 0) {
        toast.warning(`${skipped.length} imagen(es) omitida(s) por estar en uso en documentos`);
      }
      if (deleted.length === 0 && skipped.length === 0) {
        toast.info('No se eliminó ninguna imagen');
      }
      closeCleanup();
      fetchImages();
    } catch (e) {
      console.error('Error batch deleting:', e);
      toast.error(e.response?.data?.detail || 'Error al eliminar imágenes');
    } finally {
      setBatchDeleting(false);
    }
  };

  const getSourceBadge = (source) => {
    const badges = {
      upload: { label: 'Subida', color: 'bg-blue-500' },
      generated: { label: 'Generada', color: 'bg-purple-500' },
      url: { label: 'URL', color: 'bg-green-500' },
    };
    const badge = badges[source] || { label: source, color: 'bg-gray-500' };
    return (
      <span className={`text-xs px-2 py-1 rounded ${badge.color} text-white`}>
        {badge.label}
      </span>
    );
  };

  return (
    <>
    {!cleanupOnly && (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-0 max-w-6xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-blue-500" />
            Galería de Imágenes
          </DialogTitle>
          <DialogDescription>
            Selecciona una imagen de tu galería para insertar en el documento
          </DialogDescription>
        </DialogHeader>

        {/* Cleanup trigger */}
        <div className="flex justify-end pb-2">
          <Button
            variant="outline"
            size="sm"
            onClick={openCleanup}
            className="text-red-600 border-red-300 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-950/40"
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Limpiar galería
          </Button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-slate-400">
              <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
              <p>No hay imágenes en tu galería</p>
              <p className="text-sm mt-2">Las imágenes generadas o subidas aparecerán aquí</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  className={`relative group border rounded-lg overflow-hidden bg-white/50 dark:bg-slate-800/50 hover:shadow-lg transition-all cursor-pointer ${
                    selectedImageForPreview?.id === image.id ? 'ring-2 ring-blue-500' : ''
                  }`}
                  onClick={() => handleSelectImage(image)}
                >
                  {/* Image */}
                  <div className="aspect-square bg-gray-100 dark:bg-slate-700 flex items-center justify-center overflow-hidden">
                    <img
                      src={image.url}
                      alt={image.alt_text || 'Image'}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="160" height="100"%3E%3Crect fill="%23ddd" width="160" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-family="sans-serif" font-size="11"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
                      }}
                    />
                  </div>

                  {/* Info overlay */}
                  <div className="p-2 space-y-2">
                    {/* Source badge */}
                    <div className="flex items-center justify-between">
                      {getSourceBadge(image.source)}
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(image);
                          }}
                          className="p-1 hover:bg-blue-500 hover:text-white rounded transition-colors"
                          title="Editar"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        {image.path && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              downloadAsset(image.path, image.path.split('/').pop());
                            }}
                            className="p-1 hover:bg-blue-500 hover:text-white rounded transition-colors"
                            title="Descargar"
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteConfirmImage(image);
                          }}
                          className="p-1 hover:bg-red-500 hover:text-white rounded transition-colors"
                          title="Eliminar"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    {/* Alt text or edit form */}
                    {editingId === image.id ? (
                      <div className="space-y-2" onClick={(e) => e.stopPropagation()}>
                        <Input
                          value={editAltText}
                          onChange={(e) => setEditAltText(e.target.value)}
                          placeholder="Texto alternativo"
                          className="text-xs"
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(image.id)}
                            className="flex-1 bg-green-500 hover:bg-green-600"
                          >
                            <Check className="w-3 h-3 mr-1" />
                            Guardar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancelEdit}
                            className="flex-1"
                          >
                            <X className="w-3 h-3 mr-1" />
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-gray-600 dark:text-slate-300 line-clamp-2" title={image.alt_text}>
                        {image.alt_text || 'Sin descripción'}
                      </p>
                    )}

                    {/* Show prompt for generated images */}
                    {image.source === 'generated' && image.prompt && (
                      <p className="text-xs text-gray-500 dark:text-slate-400 italic line-clamp-1" title={image.prompt}>
                        Prompt: {image.prompt}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Preview and actions */}
        {selectedImageForPreview && (
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-start gap-4">
              <img
                src={selectedImageForPreview.url}
                alt={selectedImageForPreview.alt_text || 'Preview'}
                className="w-32 h-32 object-cover rounded border"
              />
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold mb-1">Vista Previa</h4>
                <p className="text-sm text-gray-600 dark:text-slate-300 mb-2">
                  {selectedImageForPreview.alt_text || 'Sin descripción'}
                </p>
                <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                  URL: {selectedImageForPreview.url}
                </p>
                {selectedImageForPreview.source === 'generated' && selectedImageForPreview.prompt && (
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Prompt: {selectedImageForPreview.prompt}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-2 pt-4 border-t">
          <Button
            variant="outline"
            onClick={() => {
              onOpenChange(false);
              setSelectedImageForPreview(null);
            }}
            className="flex-1"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleInsertImage}
            className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700"
            disabled={!selectedImageForPreview}
          >
            Insertar Imagen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    )}

    {/* Delete Confirmation Modal (individual) */}
    <Dialog open={!!deleteConfirmImage} onOpenChange={(open) => !open && setDeleteConfirmImage(null)}>
      <DialogContent className="glass border-0 max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Confirmar Eliminación
          </DialogTitle>
          <DialogDescription>
            Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {deleteConfirmImage && (
            <>
              <div className="flex items-start gap-4 p-4 bg-red-50 dark:bg-red-950/40 rounded-lg border border-red-200 dark:border-red-800">
                <img
                  src={deleteConfirmImage.url}
                  alt={deleteConfirmImage.alt_text || 'Image'}
                  className="w-20 h-20 object-cover rounded"
                />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm mb-1">
                    {deleteConfirmImage.alt_text || 'Sin descripción'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-slate-300 truncate">
                    {deleteConfirmImage.url}
                  </p>
                </div>
              </div>

              <p className="text-sm text-gray-700 dark:text-slate-200">
                ¿Estás seguro de que deseas eliminar esta imagen de tu galería?
              </p>
            </>
          )}

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmImage(null)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={() => deleteConfirmImage && handleDelete(deleteConfirmImage.id)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Eliminar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>

    {/* --- Gallery Cleanup Modal --- */}
    <Dialog open={cleanupOpen} onOpenChange={(o) => { if (!o) closeCleanup(); }}>
      <DialogContent className="glass border-0 max-w-6xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <ImageOff className="w-5 h-5" />
            Limpiar Galería
          </DialogTitle>
          <DialogDescription>
            Selecciona las imágenes que ya no usas. Las que están en uso en documentos no se pueden eliminar.
          </DialogDescription>
        </DialogHeader>

        {confirmStep ? (
          /* --- Confirmation step --- */
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            <div className="flex items-start gap-3 p-4 bg-amber-50 dark:bg-amber-950/40 rounded-lg border border-amber-200 dark:border-amber-800">
              <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
              <div className="text-sm text-gray-700 dark:text-slate-200">
                <p className="font-semibold mb-1">Confirmar eliminación</p>
                <p>Se eliminarán <strong>{selectedIds.size}</strong> imagen(es) de tu galería.</p>
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Las imágenes que están en uso en documentos NO se eliminarán.
                </p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setConfirmStep(false)}
                className="flex-1"
                disabled={batchDeleting}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleBatchDelete}
                disabled={batchDeleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {batchDeleting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4 mr-2" />
                )}
                Eliminar {selectedIds.size} imagen(es)
              </Button>
            </div>
          </div>
        ) : (
          /* --- Selection step --- */
          <>
            <div className="flex items-center justify-between gap-2 px-4 pb-3 border-b">
              <Button
                variant="outline"
                size="sm"
                onClick={toggleSelectAll}
                disabled={usageLoading || images.length === 0}
              >
                {allSelectableSelected() ? 'Deseleccionar todo' : 'Seleccionar todo'}
              </Button>
              <span className="text-sm text-gray-500 dark:text-slate-400">
                {selectedIds.size} seleccionada(s)
              </span>
              <Button
                size="sm"
                onClick={() => setConfirmStep(true)}
                disabled={selectedIds.size === 0}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Eliminar seleccionadas ({selectedIds.size})
              </Button>
            </div>

            <div className="flex-1 overflow-y-auto">
              {usageLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                </div>
              ) : images.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-slate-400">
                  <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p>No hay imágenes en tu galería</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
                  {images.map((image) => {
                    const referenced = isReferenced(image.id);
                    const usage = usageMap[image.id] || [];
                    const firstDoc = usage[0];
                    const extraDocs = usage.length - 1;
                    return (
                      <div
                        key={image.id}
                        className={`relative group border rounded-lg overflow-hidden bg-white/50 dark:bg-slate-800/50 transition-all ${
                          referenced ? 'opacity-60' : 'hover:shadow-lg'
                        }`}
                      >
                        {/* Checkbox overlay */}
                        <div className="absolute top-2 left-2 z-10">
                          <Checkbox
                            checked={selectedIds.has(image.id)}
                            onCheckedChange={() => {
                              if (!referenced) toggleSelect(image.id);
                            }}
                            disabled={referenced}
                            className="bg-white/80 dark:bg-slate-900/80"
                          />
                        </div>

                        {/* Referenced badge */}
                        {referenced && (
                          <div
                            className="absolute top-2 right-2 z-10 max-w-[70%]"
                            title={`En uso en: ${usage.map((d) => d.title).join(', ')}`}
                          >
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-amber-500/90 text-white">
                              <FileText className="w-3 h-3 shrink-0" />
                              <span className="truncate">{firstDoc?.title || 'En uso'}</span>
                              {extraDocs > 0 && ` +${extraDocs}`}
                            </span>
                          </div>
                        )}

                        {/* Image - click opens lightbox */}
                        <div
                          className="aspect-square bg-gray-100 dark:bg-slate-700 flex items-center justify-center overflow-hidden cursor-pointer"
                          onClick={() => openLightbox(image)}
                        >
                          <img
                            src={image.url}
                            alt={image.alt_text || 'Image'}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="160" height="100"%3E%3Crect fill="%23ddd" width="160" height="100"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" text-anchor="middle" dy=".3em" font-family="sans-serif" font-size="11"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
                            }}
                          />
                        </div>

                        {/* Alt text */}
                        <div className="p-2">
                          <p
                            className="text-xs text-gray-600 dark:text-slate-300 line-clamp-1"
                            title={image.alt_text}
                          >
                            {image.alt_text || 'Sin descripción'}
                          </p>
                          {referenced ? (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              En uso — no se puede eliminar
                            </p>
                          ) : (
                            <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                              {image.source === 'generated' ? 'Generada' : image.source === 'upload' ? 'Subida' : 'URL'}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
    </>
  );
};

export default ImageGallery;
